"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { LazyMotion, MotionConfig, domAnimation, m, useReducedMotion } from "motion/react";
import { isValidLaunchHandoffRect, type LaunchHandoffRect } from "@/lib/launch/launchHandoff";
import { useLaunchHandoffStore } from "@/stores/useLaunchHandoffStore";
import { cn } from "@/lib/utils";
function motionRect(rect: LaunchHandoffRect) {
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height
  };
}
function LaunchHandoffSurrogateCard(t0) {
  const $ = _c(18);
  const {
    previewText,
    fileNames,
    contextLabels,
    uploading,
    className
  } = t0;
  let t1;
  if ($[0] !== contextLabels || $[1] !== fileNames) {
    t1 = [...contextLabels.map(_temp), ...fileNames.map(_temp2)];
    $[0] = contextLabels;
    $[1] = fileNames;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const chips = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("flex h-full w-full flex-col gap-1.5 rounded-2xl border-2 border-border bg-background px-3 py-2 shadow-lg", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== chips) {
    t3 = chips.length > 0 ? <ul className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto px-0.5 pt-0.5" aria-hidden={true}>{chips.map(_temp3)}</ul> : null;
    $[5] = chips;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== uploading) {
    t4 = uploading ? <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden={true} /> : null;
    $[7] = uploading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = previewText || (chips.length > 0 ? "Uploading documents\u2026" : "Starting conversation\u2026");
  let t6;
  if ($[9] !== t5) {
    t6 = <p className="line-clamp-4 text-base leading-normal text-foreground">{t5}</p>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <div className="flex min-h-0 flex-1 flex-col justify-end px-0.5 pt-1.5"><div className="flex items-start gap-2 pb-1">{t4}{t6}</div></div>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t7) {
    t8 = <div className={t2}>{t3}{t7}</div>;
    $[14] = t2;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}

/**
 * Portal flight surrogate animating from the Overview intent field to the workbench composer.
 */
function _temp3(chip) {
  return <li key={chip.key} className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted/70 px-2 py-0.5 text-xs text-foreground"><span className="truncate">{chip.label}</span></li>;
}
function _temp2(name, index_0) {
  return {
    key: `file:${index_0}:${name}`,
    label: name
  };
}
function _temp(label, index) {
  return {
    key: `context:${index}:${label}`,
    label
  };
}
export function LaunchHandoffFlight() {
  const $ = _c(53);
  const reduceMotion = useReducedMotion();
  const phase = useLaunchHandoffStore(_temp4);
  const mode = useLaunchHandoffStore(_temp5);
  const fromRect = useLaunchHandoffStore(_temp6);
  const toRect = useLaunchHandoffStore(_temp7);
  const surrogate = useLaunchHandoffStore(_temp8);
  const completeFlight = useLaunchHandoffStore(_temp9);
  const markReady = useLaunchHandoffStore(_temp10);
  const [mounted, setMounted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setMounted(true);
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const showFlight = mode === "motion" && !reduceMotion && (phase === "preparing" || phase === "navigating" || phase === "flying" || phase === "settling");
  let t2;
  let t3;
  if ($[2] !== fromRect || $[3] !== markReady || $[4] !== mode || $[5] !== phase || $[6] !== reduceMotion || $[7] !== toRect) {
    t2 = () => {
      if (phase !== "navigating" || mode !== "motion" || reduceMotion) {
        return;
      }
      if (!isValidLaunchHandoffRect(fromRect) && isValidLaunchHandoffRect(toRect)) {
        markReady();
      }
    };
    t3 = [phase, mode, reduceMotion, fromRect, toRect, markReady];
    $[2] = fromRect;
    $[3] = markReady;
    $[4] = mode;
    $[5] = phase;
    $[6] = reduceMotion;
    $[7] = toRect;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== completeFlight || $[11] !== fromRect || $[12] !== phase || $[13] !== toRect) {
    t4 = () => {
      if (phase !== "flying") {
        return;
      }
      if (!isValidLaunchHandoffRect(fromRect) && isValidLaunchHandoffRect(toRect)) {
        completeFlight();
      }
    };
    t5 = [phase, fromRect, toRect, completeFlight];
    $[10] = completeFlight;
    $[11] = fromRect;
    $[12] = phase;
    $[13] = toRect;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  if (!mounted || !showFlight) {
    return null;
  }
  let t6;
  if ($[16] !== fromRect || $[17] !== toRect) {
    t6 = isValidLaunchHandoffRect(fromRect) ? fromRect : toRect;
    $[16] = fromRect;
    $[17] = toRect;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const holdRect = t6;
  if (!isValidLaunchHandoffRect(holdRect)) {
    return null;
  }
  let t7;
  if ($[19] !== fromRect || $[20] !== phase || $[21] !== toRect) {
    t7 = phase === "flying" && isValidLaunchHandoffRect(fromRect) && isValidLaunchHandoffRect(toRect);
    $[19] = fromRect;
    $[20] = phase;
    $[21] = toRect;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const isFlying = t7;
  let t8;
  if ($[23] !== phase || $[24] !== toRect) {
    t8 = phase === "settling" && isValidLaunchHandoffRect(toRect);
    $[23] = phase;
    $[24] = toRect;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const isSettling = t8;
  const t9 = phase === "flying" ? `fly-${toRect?.top}` : `hold-${holdRect.top}`;
  let t10;
  if ($[26] !== fromRect || $[27] !== holdRect || $[28] !== isFlying) {
    t10 = isFlying ? {
      ...motionRect(fromRect),
      opacity: 1
    } : {
      ...motionRect(holdRect),
      opacity: 1
    };
    $[26] = fromRect;
    $[27] = holdRect;
    $[28] = isFlying;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== holdRect || $[31] !== isFlying || $[32] !== isSettling || $[33] !== toRect) {
    t11 = isSettling ? {
      ...motionRect(toRect),
      opacity: 0
    } : isFlying ? {
      ...motionRect(toRect),
      opacity: 1
    } : {
      ...motionRect(holdRect),
      opacity: 1
    };
    $[30] = holdRect;
    $[31] = isFlying;
    $[32] = isSettling;
    $[33] = toRect;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const t12 = isSettling ? 0.08 : 0;
  let t13;
  if ($[35] !== t12) {
    t13 = {
      type: "spring",
      stiffness: 520,
      damping: 38,
      opacity: {
        duration: t12
      }
    };
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== completeFlight || $[38] !== isFlying || $[39] !== phase) {
    t14 = () => {
      if (phase === "flying" && isFlying) {
        completeFlight();
      }
    };
    $[37] = completeFlight;
    $[38] = isFlying;
    $[39] = phase;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== surrogate.contextLabels || $[42] !== surrogate.fileNames || $[43] !== surrogate.previewText || $[44] !== surrogate.uploading) {
    t15 = <LaunchHandoffSurrogateCard previewText={surrogate.previewText} fileNames={surrogate.fileNames} contextLabels={surrogate.contextLabels} uploading={surrogate.uploading} className="h-full w-full" />;
    $[41] = surrogate.contextLabels;
    $[42] = surrogate.fileNames;
    $[43] = surrogate.previewText;
    $[44] = surrogate.uploading;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t10 || $[47] !== t11 || $[48] !== t13 || $[49] !== t14 || $[50] !== t15 || $[51] !== t9) {
    const content = <LazyMotion features={domAnimation}><MotionConfig reducedMotion="user"><m.div key={t9} className="pointer-events-none fixed z-[60]" initial={t10} animate={t11} transition={t13} onAnimationComplete={t14}>{t15}</m.div></MotionConfig></LazyMotion>;
    t16 = createPortal(content, document.body);
    $[46] = t10;
    $[47] = t11;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t9;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  return t16;
}
function _temp10(s_5) {
  return s_5.markReady;
}
function _temp9(s_4) {
  return s_4.completeFlight;
}
function _temp8(s_3) {
  return s_3.surrogate;
}
function _temp7(s_2) {
  return s_2.toRect;
}
function _temp6(s_1) {
  return s_1.fromRect;
}
function _temp5(s_0) {
  return s_0.mode;
}
function _temp4(s) {
  return s.phase;
}